import React from "react";
import { Box } from "@UI/Components/Box/Box";
import { Stack, HStack, VStack } from "@UI/Components/Stack/Stack";
import { Icon } from "@UI/Components/Icon/Icon";
import { Star } from "@UI/Components/Icons/Star";
import { Text, TextProps } from "@UI/Components/Text/Text";
import { SuperTalentPillBadge } from "@modules/ServiceProviders/Components/SuperTalent/SuperTalentPillBadge";
import { Link } from "@UI/Components/Link/Link";
import { Tooltip } from "@UI/Components/Tooltip/Tooltip";
import { ICandidate } from "@modules/ServiceRequest/types";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import messages from "../messages";
import useI18n from "@I18n/hooks/useI18n";

export const CandidateProfileSection = (
  props: ICandidate & {
    avatarSection: React.ReactNode;
    offerSection?: React.ReactNode;
    profileNameStyle?: TextProps;
    ratingStyle?: TextProps;
    badgeStyle?: TextProps;
    showSeeProfileText?: boolean;
    isActivationRateEnabled?: boolean;
  }
) => {
  const { formatMessage } = useI18n();

  return (
    <Box flexDir="row">
      <HStack spacing={{ base: "16px" }}>
        {props.avatarSection}
        <VStack spacing="4px" alignItems="start">
          <Stack
            flexDir={{ base: "column", lg: "row" }}
            flexWrap="wrap"
            spacing="0"
            columnGap="8px"
            rowGap="4px"
          >
            <HStack textAlign="start" alignItems="start">
              <Tooltip label={props.firstName}>
                <Text
                  color="gray.800"
                  fontWeight="bold"
                  fontSize={{ base: "lg", lg: "xl" }}
                  m="0"
                  noOfLines={2}
                  {...props.profileNameStyle}
                >
                  {props.firstName}
                </Text>
              </Tooltip>

              {props.pro && (
                <Text
                  fontWeight="700"
                  fontSize={{ base: "xs", lg: "sm" }}
                  borderRadius="5px"
                  py="1px"
                  px="6px"
                  background="#0098FE"
                  color="white"
                  alignSelf="start"
                  {...props.badgeStyle}
                >
                  PRO
                </Text>
              )}
            </HStack>
            {props.superTalent && !props.isActivationRateEnabled && (
              <SuperTalentPillBadge
                popoverContent={
                  <VStack
                    justifyContent="center"
                    maxW="190px"
                    spacing="0"
                    color="white"
                    fontSize="sm"
                  >
                    <Text textAlign="center" m="0">
                      <FormattedMessage
                        {...messages[
                          "serviceRequest.superTalentBadge.popoverDescription"
                        ]}
                      />
                    </Text>
                    <Link
                      h="fit-content"
                      textDecoration="underline"
                      target="_blank"
                      href={formatMessage(
                        messages["serviceRequest.superTalentBadge.popoverLink"]
                      )}
                    >
                      <Text m="0" display="inline">
                        <FormattedMessage
                          {...messages[
                            "serviceRequest.superTalentBadge.popoverLinkCTA"
                          ]}
                        />
                      </Text>
                    </Link>
                  </VStack>
                }
              />
            )}
          </Stack>
          <HStack spacing="5px">
            <Icon as={Star} color="orange" width="15px" height="14px" />
            <HStack spacing="4px" textAlign="start">
              <Text
                as="h5"
                m="0"
                fontWeight="700"
                fontSize={{ base: "md", md: "lg" }}
                {...props.ratingStyle}
              >
                {props.ratingsCount > 0 ? (
                  props.ratingsAverage?.toFixed(2)
                ) : (
                  <FormattedMessage {...messages["newTalent"]} />
                )}
              </Text>
              <Text
                display="inline"
                fontSize={{ base: "md", md: "lg" }}
                {...props.ratingStyle}
              >
                {props.ratingsCount > 0 && `(${props.ratingsCount})`}
              </Text>
            </HStack>
          </HStack>

          {props.offerSection}

          {props.showSeeProfileText && (
            <Link
              h="fit-content"
              textDecoration="underline"
              color="pink.500"
              _hover={{ color: "pink.700" }}
            >
              <Text m="0" display="inline">
                <FormattedMessage {...messages["seeProfile"]} />
              </Text>
            </Link>
          )}
        </VStack>
      </HStack>
    </Box>
  );
};
